import styled from 'styled-components';
import { withQuery, select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import { site } from '@panel/commons/helpers';
import AsteriskBullet from '@panel/commons/assets/icons/asterisk_bullet.svg';
import arrowLeft from '@panel/commons/assets/icons/chevronLeft.svg';
import arrowRight from '@panel/commons/assets/icons/chevronRight.svg';
import checkGreen from '@panel/commons/assets/icons/checkGreen.svg';
import file from '@panel/commons/assets/icons/file.svg';
import money from '@panel/commons/assets/icons/money.svg';
import eyeSmall from '@panel/commons/assets/icons/eye_small.svg';
import { DetectableOverflow } from '../../../utils/DetectableOverflow';

const Body = styled.div`
  padding: 40px 40px 45px;
  ${withQuery.xs.down()} {
    padding: 40px 16px;
  }
  overflow: hidden;
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: ${props => (props.separator && '8px') || (props.upgrade && '10px') || '0px'};
  white-space: normal;
  font-weight: 600;
  margin-top: ${props => (props.separator && '64px') || (props.upgrade && '-30px')};
  line-height: 23px;
  ${withQuery.md.down()} {
    font-size: ${site.isMexico ? '18px' : '2rem'};
  }
  ${withQuery.xs.down()} {
    font-weight: ${site.isMexico ? '500' : '600'};
    font-size: ${site.isMexico ? '18px' : '20px'};
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SubTitle = styled.h3`
  font-weight: 600;
  font-size: ${props => (props.upgrade ? '20px' : '1.4rem')};
  font-family: 'Hind';
  margin-top: 32px;
  margin-bottom: 16px;
`;

const PlanContainer = styled.div`
  margin-bottom: ${props => (props.isCurrentPlan ? '64px' : '24px')};
`;

const Disponibles = styled.div`
  font-family: 'Hind';
  font-style: normal;
  font-weight: 500;
  font-size: 1.2rem;
  color: ${select('darkGray')};
`;

const PlanRow = styled.div`
  border: 1px solid ${select('midLightGray')};
  border-left: 10px solid ${select('midLightGray')};
  border-radius: 8px;
  height: 72px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 22px;
  margin-bottom: 8px;
  cursor: ${props => !props.selected && 'pointer'};
  border-left-color: ${select('midLightGray')};
  label {
    cursor: ${props => !props.selected && 'pointer'};
    display: flex;
    align-items: center;
  }
  &.SUPERHIGHLIGHTED {
    border-left-color: ${select('navent')};
  }
  &.HIGHLIGHTED {
    border-left-color: ${select('snackGreen')};
  }
  &.SIMPLE {
    border-left-color: ${select('primary')};
  }
  box-shadow: ${props => props.selected && '-1px 2px 20px rgba(0, 0, 0, 0.08)'};
  ${withQuery.xs.up()} {
    min-width: 525px;
  }
  ${withQuery.xs.down()} {
    min-width: 210px;
    padding: 16px 16px 16px 42px;
    flex-direction: column;
    height: 136px;
    align-items: flex-start;
  }
`;

const PlanInfo = styled.div`
  border: ${props => props.plan === 'SUPERHIGHLIGHTED' && `1px solid ${getColor('primary')}`};
  border-radius: 9px;
  padding: 3px 12px;
  font-family: 'Hind';
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  min-width: 130px;
  color: ${props => {
    if (props.selected) {
      return props.plan === 'SUPERHIGHLIGHTED' ? getColor('primary') : select('black');
    }
    return props.plan !== 'SUPERHIGHLIGHTED' ? select('black') : getColor('primary');
  }};
  ${withQuery.xs.down()} {
    order: ${props => props.orden};
    padding: ${props => (props.plan !== 'SUPERHIGHLIGHTED' ? '0px' : '7px 15px')};
  }
`;

const SubTitleSuggestion = styled.div`
  font-family: 'Hind';
  font-style: normal;
  font-weight: ${props => (props.upgrade ? '400' : 'normal')};
  font-size: ${props => (props.upgrade ? '16px' : '1.4rem')};
  margin-bottom: ${props => (props.upgrade ? '0' : '12px')};
  color: ${select('darkGray')};
  white-space: normal;
`;

const OtherPlansContainer = styled.div`
  min-height: 340px;
  max-width: 100%;
  overflow: auto;
  overflow-y: visible !important;
  padding-top: ${props => (props.isBrasil ? '35px' : '20px')};
  display: inline-block;
  scroll-behavior: smooth;
  touch-action: none;
  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  > div {
    display: inline-block;
    margin-right: 40px;
    height: 99%;
  }
  ${withQuery.lg.down()} {
    scroll-snap-type: x mandatory;
    touch-action: inherit;
  }
`;
const OverflowWrapper = styled(DetectableOverflow)`
  width: 100%;
  display: inline-block;
  overflow: visible !important;
`;

const PlanCard = styled.div`
  height: ${props => {
    if (props.upgrade) return '382px';
    if (props.siteId === 'ZPAR') return '360px';
    return '335px';
  }};
  width: 100%;
  border: 1px solid ${select('middleGray')};
  box-shadow: 1px 2px 16px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  position: relative;
  &:last-of-type {
    margin-right: 0px;
  }
  ${withQuery.xs.down()} {
    width: 245px;
    margin-right: 32px;
  }
`;

const PlanInner = styled.div`
  height: 100%;
  width: 100%;
  max-width: 261px;
  display: flex;
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  padding: 16px 21px 16px 19px;
  position: relative;
`;

const PlanName = styled.div`
  height: 18px;
  font-family: 'Hind';
  font-style: normal;
  font-size: ${props => (props.upgradeName ? '14px' : '1.2rem')};
  color: ${select('darkGray')};
  font-weight: 600;
  &.SUPERHIGHLIGHTED {
    color: ${select('navent')};
  }
  &.HIGHLIGHTED {
    color: ${select('positive')};
  }
  &.SIMPLE {
    color: ${select('darkYellow')};
  }
`;

const Price = styled.div`
  height: ${props => (props.oldPrice ? '23px' : '45px')};
  margin-top: ${props => (props.oldPrice ? '5px' : '8px')};
  font-family: 'Hind';
  font-size: ${props => {
    if (props.oldPrice) return '14px';
    if (props.isMobile) return '2.8rem';
    return '3.1rem';
  }};
  font-style: normal;
  font-weight: ${props => (props.oldPrice ? '600' : '700')};
  line-height: ${props => (props.oldPrice ? '23px' : '51px')};
  color: ${select('black')};
  position: relative;
  & > span {
    font-size: ${props => (props.oldPrice ? '14px' : '1.5rem')};
    font-weight: ${props => (props.oldPrice ? '600' : '400')};
    color: ${select('black')};
    position: relative;
    left: ${props => (props.oldPrice ? '0' : '1px')};
  }
  & > hr {
    border: 0;
    height: 1px;
    background: black;
    position: absolute;
    top: 11px;
    width: 55%;
  }
`;

const PriceUpgrade = styled.div`
  height: 40px;
  font-family: 'Hind';
  font-size: 28px;
  font-style: normal;
  font-weight: 700;
  line-height: 35px;
  color: ${select('blue')};
  & > span {
    font-size: 14px;
    font-weight: 400;
    color: ${select('blue')};
    position: relative;
    left: -8px;
    top: ${props => (props.recurrenceAsterikOnly ? '-10px' : '0')};
  }
`;

const TopContainer = styled.div`
  height: ${props => (props.upgrade ? '100px' : '70px')};
`;

const BottomContainer = styled.div`
  margin-top: 12px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const PlanDetailsContainer = styled.div`
  top: ${props => (props.upgrade ? '118px' : '100px')};
  display: flex;
  flex-direction: column;
  justify-content: ${props => props.full && 'space-between'};
  min-height: 115px;
  max-height: 200px;
  width: 83%;
  flex-grow: 1;
  & > div {
    margin-top: 12px;
  }
`;

const PlanDetails = styled.span`
  font-family: 'Hind';
  font-style: normal;
  font-size: ${props => (props.upgrade ? '16px' : '1.2rem')};
  color: ${select('black')};
  font-weight: ${props => (props.upgrade ? '400' : '300')};
  white-space: normal;
  font-family: Hind;
  line-height: ${props => (props.upgrade ? 'normal' : '19px')};
  display: inline-block;
  width: 100%;
  &:nth-child(1) {
    font-weight: 600;
  }
`;

const Check = styled.span`
  width: ${props => (props.upgrade ? '16px' : '10px')};
  height: ${props => (props.upgrade ? '16px' : '10px')};
  background-image: ${props =>
    (props.iconBenefit === 'file' && `url(${file})`) ||
    (props.iconBenefit === 'money' && `url(${money})`) ||
    (props.iconBenefit === 'eye' && `url(${eyeSmall})`) ||
    `url(${checkGreen})`};
  margin-right: 8px;
  display: inline-block;
  vertical-align: top;
  position: relative;
  top: 4px;
`;

const ExtraInfo = styled.div`
  height: 19px;
  margin-top: 18px;
  font-family: Hind;
  font-size: 1.2rem;
  font-style: normal;
  font-weight: 300;
  line-height: 19px;
  text-align: left;
  color: ${select('darkGray')};
  white-space: normal;
  margin-bottom: 6px;
`;

const PriceInfo = styled(ExtraInfo)`
  margin-top: -6px;
  font-size: 14px;
`;

const BuyButton = styled.a`
  cursor: pointer;
  border-radius: 12px;
  background: ${getColor('primary')};
  font-family: 'Hind';
  font-size: 1.4rem;
  font-style: normal;
  font-weight: 600;
  text-align: center;
  height: 40px;
  width: 173px;
  text-decoration: none;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${select('white')};
  align-self: center;
`;

const ScrollButton = styled.button`
  opacity: ${props => (!props.show ? '0' : '1')};
  width: ${props => (!props.show ? '0px' : '40px')};
  z-index: 500;
  cursor: pointer;
  border: 1px solid #cbd6dc;
  box-sizing: border-box;
  border-radius: 40px;
  height: 40px;
  background-color: ${select('white')};
  position: relative;
  top: 160px;
  left: ${props => props.left && '-15px'};
  right: ${props => !props.left && '25px'};
  background-image: ${props => (props.left ? `url(${arrowLeft})` : `url(${arrowRight})`)};
  background-repeat: no-repeat;
  background-position: center;
`;

const ButtonContainer = styled.div`
  width: 1px;
  height: 100%;
  overflow: visible;
  display: inline-block;
  vertical-align: top;
  ${withQuery.lg.down()} {
    display: none;
  }
`;

const CardsScrollArea = styled.div`
  width: 100%;
`;

const OnlyRentTag = styled.div`
  position: absolute;
  top: -17px;
  left: 68px;
  margin: 0 auto;
  height: 34px;
  width: 104px;
  border-radius: 4px;
  padding: 8px, 12px, 8px, 12px;
  background-color: ${select('middleGray')};
  font-family: 'Hind';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  padding: 8px 12px 8px 12px;
`;

const InfoCardContainer = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  padding: 12px;
  background-color: ${select('white')};
  border: 1px solid ${select('midLightGray')};
  border-left: 4px solid ${select('blue')};
  border-radius: 0px 4px 4px 0px;
  margin: 40px 0 24px 0;
  &:before {
    content: '';
    vertical-align: text-top;
    width: 19px;
    height: 19px;
    position: absolute;
    background-image: url(${AsteriskBullet});
    background-repeat: no-repeat;
    top: 12px;
    ${withQuery.xs.down()} {
      align-items: baseline;
    }
  }
  p {
    font-size: 14px;
    margin-left: 25px;
    white-space: break-spaces;
  }
`;

const PaymentMethodsContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px;
  border-top: 1px solid ${select('midLightGray')};
  margin-bottom: 20px;
  ${withQuery.xs.down()} {
    flex-direction: column;
    align-items: flex-start;
  }
  p {
    font-size: 16px;
    margin-right: 30px;
    font-weight: 600;
    white-space: break-spaces;
    color: ${select('darkGray')};
    ${withQuery.xs.down()} {
      margin-bottom: 10px;
    }
  }
  div {
    display: flex;
  }
  img {
    margin-right: 15px;
  }
`;

export {
  Body,
  Title,
  SubTitle,
  PlanContainer,
  Disponibles,
  PlanRow,
  PlanInfo,
  SubTitleSuggestion,
  OtherPlansContainer,
  PlanCard,
  PlanInner,
  PlanName,
  Price,
  PriceUpgrade,
  PlanDetailsContainer,
  PlanDetails,
  Check,
  ExtraInfo,
  BuyButton,
  ScrollButton,
  ButtonContainer,
  CardsScrollArea,
  OverflowWrapper,
  PriceInfo,
  BottomContainer,
  OnlyRentTag,
  InfoCardContainer,
  PaymentMethodsContainer,
  TopContainer,
  TitleContainer
};
